import React from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import PageTitle from "../../../components/pagetitle/PageTitle";
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Breadcrumbs,
  Link as MuiLink,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

const ActInfoPage = () => {
  const { chapterIndex } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const chapter = location.state?.chapter;
  const previousTab = location.state?.activeTab;
  const previousExpandedAct = location.state?.expandedAct;

  if (!chapter) {
    return (
      <Container sx={{ py: 10, textAlign: "center" }}>
        <Typography variant="h4" sx={{ mb: 4 }}>
          Chapter not found
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate("/acts")}
          sx={{ bgcolor: "var(--primary-color)", borderRadius: "50px", px: 4 }}
        >
          Return to Acts List
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: "#f8f9fa", minHeight: "100vh", pb: 10 }}>
      <PageTitle pageTitle={chapter.title} pagesub="Documentation" />

      <Container
        maxWidth="md"
        sx={{ mt: { xs: -4, md: -6 }, position: "relative", zIndex: 10 }}
      >
        {/* Navigation Actions */}
        <Box
          sx={{
            mb: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Button
            startIcon={<ArrowBackIcon sx={{ color: "white" }} />}
            onClick={() =>
              navigate("/acts", {
                state: {
                  activeTab: previousTab,
                  expandedAct: previousExpandedAct,
                },
              })
            }
            sx={{
              color: "#ffffff",
              fontWeight: 700,
              textTransform: "none",
              "&:hover": { bgcolor: "rgba(0,0,0,0.05)" },
            }}
          >
            Back to Statutes
          </Button>

          <Breadcrumbs
            separator={
              <NavigateNextIcon fontSize="small" sx={{ color: "white" }} />
            }
            aria-label="breadcrumb"
            sx={{ display: { xs: "none", md: "block" } }}
          >
            <MuiLink
              underline="hover"
              color="inherit"
              sx={{ cursor: "pointer", color: "white" }}
              onClick={() => navigate("/acts")}
            >
              Acts
            </MuiLink>
            <Typography color="white">
              Chapter {parseInt(chapterIndex) + 1}
            </Typography>
          </Breadcrumbs>
        </Box>

        {/* Content Card */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, md: 8 },
            borderRadius: "32px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.05)",
            border: "1px solid #eee",
            bgcolor: "#fff",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Decorative Border Accent */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "6px",
              bgcolor: "var(--primary-color)",
            }}
          />

          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              mb: 4,
              fontFamily: "'Roboto Slab', serif",
              color: "#222",
              fontSize: { xs: "1.75rem", md: "2.5rem" },
              lineHeight: 1.3,
            }}
          >
            {chapter.title}
          </Typography>

          <Box sx={{ mt: 5 }}>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 2,
                fontSize: "1.1rem",
                color: "#444",
                textAlign: "justify",
                whiteSpace: "pre-line",
              }}
            >
              {chapter.info}
            </Typography>
          </Box>

          <Box
            sx={{
              mt: 8,
              pt: 4,
              borderTop: "1px solid #eee",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "#999",
                letterSpacing: 1,
                fontWeight: 700,
                textTransform: "uppercase",
              }}
            >
              Reference Status
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#777", fontStyle: "italic" }}
            >
              This summary is based on the current version of the statute. For
              exact legal compliance, verify specific subsections and the latest
              amendments as of April 2024.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ActInfoPage;
